import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Message, ActiveWorkflow, WorkflowStep } from '@/types/conversations'
import { formatStepId, getWorkflowTitle } from '@/lib/workflow-utils'

interface WorkflowStatusCardProps {
  message: Message
  activeWorkflow?: ActiveWorkflow | null
}

export function WorkflowStatusCard({ message, activeWorkflow }: WorkflowStatusCardProps) {
  const { t } = useTranslation('conversations')
  const [expanded, setExpanded] = useState(false)

  const isLive = activeWorkflow && activeWorkflow.message_id === message.id
  const isHistorical = !isLive && !!message.workflow_id

  if (!isLive && !isHistorical) return null

  let title: string
  let steps: WorkflowStep[] | undefined
  let isComplete: boolean
  let isFailed: boolean
  let completedCount = 0
  let totalCount = 0

  if (isLive) {
    steps = activeWorkflow.steps_data as WorkflowStep[] | undefined
    if (!steps) return null

    isComplete = activeWorkflow.status === 'completed' || activeWorkflow.workflow_status === 'completed'
    isFailed = activeWorkflow.status === 'failed' || activeWorkflow.status === 'interrupted' ||
      !!(activeWorkflow.workflow_status && ['paused', 'reverted', 'completed_with_failures', 'interrupted'].includes(activeWorkflow.workflow_status))
    title = getWorkflowTitle(activeWorkflow.workflow_name, activeWorkflow.description)
    completedCount = steps.filter((s) => s.status === 'completed').length
    totalCount = steps.length
  } else {
    isComplete = message.status === 'completed'
    isFailed = message.status === 'failed' || message.status === 'interrupted'
    steps = message.workflow_step_data?.steps as WorkflowStep[] | undefined

    if (steps) {
      completedCount = steps.filter((s) => s.status === 'completed').length
      totalCount = steps.length
    }

    title = (() => {
      if (!isComplete) return message.content
      switch (message.workflow_name) {
        case 'security_audit':
        case 'security_audit_with_ai':
          return t('chat.workflowCompleted.securityAudit')
        case 'seo_optimize': return t('chat.workflowCompleted.seoOptimize')
        case 'fix_exception': return t('chat.workflowCompleted.fixException')
        default: return t('chat.deployed')
      }
    })()
  }

  const hasSteps = steps && steps.length > 0
  const isInProgress = !isComplete && !isFailed

  return (
    <div className="mb-3 max-w-full overflow-hidden">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <button
          type="button"
          onClick={() => hasSteps && setExpanded(!expanded)}
          className={`px-4 py-2.5 flex items-center gap-3 w-full ${hasSteps ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <StatusIcon isComplete={isComplete} isFailed={isFailed} />
          <span className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100 [overflow-wrap:anywhere] text-left">
            {title}
          </span>
          {hasSteps && (
            <>
              <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400 ml-auto">
                {completedCount}/{totalCount}
              </span>
              {isInProgress && (
                <div className="shrink-0 w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all bg-blue-500"
                    style={{ width: `${(completedCount / totalCount) * 100}%` }}
                  />
                </div>
              )}
              <svg
                className={`h-3.5 w-3.5 text-gray-400 transition-transform shrink-0 ${expanded ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </>
          )}
        </button>

        {expanded && hasSteps && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2 space-y-1.5">
            {steps!.map((step) => (
              <div key={step.id} className="flex items-center gap-2">
                <StepStatusDot status={step.status} />
                <span className={`text-xs ${
                  step.status === 'completed' ? 'text-gray-700 dark:text-gray-300' :
                  step.status === 'in_progress' ? 'text-blue-600 dark:text-blue-400' :
                  step.status === 'failed' ? 'text-red-600 dark:text-red-400' :
                  'text-gray-500 dark:text-gray-400'
                }`}>
                  {formatStepId(step.id)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function StepStatusDot({ status }: { status: WorkflowStep['status'] }) {
  if (status === 'completed') {
    return (
      <svg className="h-3.5 w-3.5 text-green-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    )
  }
  if (status === 'failed') {
    return (
      <svg className="h-3.5 w-3.5 text-red-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    )
  }
  if (status === 'in_progress') {
    return (
      <svg className="h-3.5 w-3.5 text-blue-500 animate-spin shrink-0" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    )
  }
  return <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 dark:border-gray-600 shrink-0" />
}

function StatusIcon({ isComplete, isFailed }: { isComplete: boolean; isFailed: boolean }) {
  if (isComplete) {
    return (
      <div className="shrink-0">
        <svg className="h-4 w-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22,4 12,14.01 9,11.01" />
        </svg>
      </div>
    )
  }
  if (isFailed) {
    return (
      <div className="shrink-0">
        <svg className="h-4 w-4 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
    )
  }
  return (
    <div className="shrink-0">
      <svg className="h-4 w-4 text-blue-500 animate-spin" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  )
}
